import { IFlight } from "@vmosolutions/oc-interfaces";
import { Grid, styled, Avatar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMemo, useState, useCallback } from "react";
import moment from "moment-timezone";
import { invertHexColor } from "src/pages/FlightTimeline/TimelineUtils";
import FlightIcon from "@mui/icons-material/Flight";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { defaultTimezone } from "src/redux/reducers/CrewScheduleReducer";
import { formatDateString } from "../DateDisplay";
import _ from "lodash";

export interface IFlightCard {
  id?: string;
  flight?: IFlight;
  drop?: boolean;
  onClick?: any;
  selectedFlights: IFlight[];
  trackedFlights: IFlight[];
  setTrackedFlights: React.Dispatch<React.SetStateAction<IFlight[]>>;
}

const OCFlightCard = styled(Grid)(({ theme }) => ({
  userSelect: "none",
  width: "175px",
  height: "90px",
  boxShadow: "2px 2px 5px " + theme.palette.common.black,
  borderRadius: "2px",
  padding: "5px 10px",
  fontFamily: "B612 Mono, arial, sans-serif",
  fontSize: 10,
  fontWeight: "normal",
  alignItems: "center",
  justifyContent: "space-between",
  position: "relative",
  "&.selected": {
    outline: `2px solid ${theme.custom.modal.warning}`,
  },
  "&:hover": {
    outline: "2px solid white",
  },
  "& .aircraft_icon": {
    position: "absolute",
    marginTop: "-5px",
    marginLeft: "-23px",
    transform: "rotate(50deg) scale(1.2)",
    opacity: 0.2,
    "& path": { fill: "black" },
  },
  "& .dateRow": {
    fontSize: 11,
  },
  "& .arrowRow": {
    fontSize: 13,
    maxHeight: "13px",
  },
  "& .firstRow": {
    marginBottom: "0px",
  },
  "& .info": {
    width: "120px",
    justifyContent: "center",
    paddingTop: "1px",
    "& .origin": {
      zIndex: 5,
    },
    "& .flightNumber": {
      fontSize: 12,
      fontWeight: "bold",
      padding: "0px 0px",
      marginTop: "-2px",
    },
  },
  "& .closeButton": {
    position: "absolute",
    top: 1,
    left: 1,
    width: "30px",
    height: "30px",
    color: "white",
    backgroundColor: "#333333",
    zIndex: 10,
    cursor: "pointer",
  },
}));

export const FlightCard = (props: IFlightCard): JSX.Element => {
  const { id, flight, drop, onClick, selectedFlights, trackedFlights, setTrackedFlights } = props;

  const [hovered, setHovered] = useState<boolean>(false);

  const color = flight?.flightType?.color;
  const frColor = color ? invertHexColor(color) : "#FFFFFF";
  const bgColor = color ?? "000000";

  const removeFlight = useCallback(() => {
    const tempFlights = _.cloneDeep(trackedFlights);
    tempFlights.splice(tempFlights.indexOf(flight), 1);
    setTrackedFlights(tempFlights);
  }, [trackedFlights, flight]);

  const selected = useMemo(() => {
    const thisFlight = selectedFlights.find((trackedFlight: IFlight) => trackedFlight._id === flight._id);
    return !!thisFlight;
  }, [selectedFlights]);

  const startLocal = useMemo(() => {
    return flight.actualOut && flight.originAirport?.timezoneId
      ? moment.utc(flight.actualOut).tz(flight.originAirport?.timezoneId)?.format("DD-MMM-YYYY HH:mm z")
      : null;
  }, [flight]);

  const endLocal = useMemo(() => {
    return flight.actualIn && flight.destAirport?.timezoneId
      ? moment.utc(flight.actualIn).tz(flight.destAirport?.timezoneId)?.format("DD-MMM-YYYY HH:mm z")
      : null;
  }, [flight]);

  const outUTC = useMemo(() => {
    return flight.actualOut ? moment.utc(flight.actualOut).format("DD-MMM-YYYY HH:mm [Z]") : null;
  }, [flight]);

  const inUTC = useMemo(() => {
    return flight.actualIn ? moment.utc(flight.actualIn).format("DD-MMM-YYYY HH:mm [Z]") : null;
  }, [flight]);

  return (
    <OCFlightCard
      onClick={onClick}
      container
      direction="column"
      wrap="nowrap"
      className={selected && "selected"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        color: `${frColor} !important`,
        backgroundColor: `#${bgColor} !important`,
        textShadow: `${frColor == "#FFFFFF" ? "1px 1px 2px black" : "inherit"}`,
      }}
    >
      <Grid item className="firstRow">
        <FlightIcon className="aircraft_icon" />
        <Grid container className="info">
          <Grid item xs={4} className="origin">
            {flight.origin}
          </Grid>
          <Grid item xs={4} className="flightNumber">
            {flight.flightNumber}
          </Grid>
          <Grid item className="destination">
            {flight.destination}
          </Grid>
        </Grid>
      </Grid>
      <Grid item className="dateRow">
        {startLocal}
      </Grid>
      <Grid item className="dateRow">
        {outUTC}
      </Grid>
      <Grid item className="dateRow">
        {endLocal}
      </Grid>
      <Grid item className="dateRow">
        {inUTC}
      </Grid>
      {hovered && (
        <Avatar className={"closeButton"} onClick={removeFlight}>
          <CloseIcon sx={{ fontWeight: "bold" }} />
        </Avatar>
      )}
    </OCFlightCard>
  );
};
